/**
 * Appsmith-style data binding for list/chart widgets (ADR-0044 U1).
 *
 * A widget's data source is a `{{ }}` BINDING ("Table Data = {{ getUsers.data }}").
 * Two shapes are recognised:
 *
 *  1. A QUERY reference — `{{ $queries.<name>.data }}` / `.rows` / bare
 *     `{{ $queries.<name> }}`. `queryRefFromBinding` extracts `<name>` so the
 *     runtime keeps the FULL reactive query path (server search / sort /
 *     pagination / deps).
 *  2. Any OTHER array expression — `{{ Deals.fn() }}`, `{{ $queries.x.rows.filter(...) }}`
 *     — which the runtime evaluates to a static client-side list.
 *
 * Pure + unit-tested; shared by the renderer, the inspector and the ref graph so
 * the three can't disagree on what a binding points at.
 */

/** The canonical binding for a page query (what the inspector writes/migrates to). */
export function bindingForQueryRef(name: string): string {
  return `{{ $queries.${name}.data }}`;
}

// `{{  $queries.recent.data  }}` → "recent". Tolerates surrounding whitespace and
// an optional trailing `.data` / `.rows`; rejects anything more complex (a call,
// an index, a longer path) so only a PLAIN query reference takes the reactive
// fast-path — everything else is an arbitrary array expression.
const QUERY_REF_RE = /^\{\{\s*\$queries\.([A-Za-z_$][\w$]*)(?:\.(?:data|rows))?\s*\}\}$/;

/**
 * If `expr` is a plain `$queries.<name>` binding, return `<name>`; else null
 * (empty, a non-binding literal, or a richer expression → not a query ref).
 */
export function queryRefFromBinding(expr: unknown): string | null {
  if (typeof expr !== "string") return null;
  const m = expr.trim().match(QUERY_REF_RE);
  return m ? m[1] : null;
}

/**
 * The effective query name a widget reads: the `data` binding's query ref when
 * it is one (the ONLY data source — ADR-0044 U1). Empty string = no query (a
 * static `data` array or unset).
 */
export function effectiveQueryRef(props: Record<string, unknown>): string {
  return queryRefFromBinding(props.data) ?? "";
}

/**
 * True when `data` holds an arbitrary array expression (a binding that is NOT a
 * plain query ref) — the runtime should evaluate it to a static list.
 */
export function isArbitraryDataBinding(props: Record<string, unknown>): boolean {
  const d = props.data;
  return typeof d === "string" && d.includes("{{") && queryRefFromBinding(d) === null;
}
